import {useEffect, useState, useCallback} from 'react';

const POLL_INTERVAL = 60_000;
const API_URL = 'https://uptime.betterstack.com/widgets/announcement?id=237381';
const DISMISSED_KEY = 'betterstack_dismissed_digest';

interface Announcement {
  html: string;
  digest: string;
  link: string;
}

export function StatusAnnouncement() {
  const [announcement, setAnnouncement] = useState<Announcement | null>(null);
  const [dismissedDigests, setDismissedDigests] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem(DISMISSED_KEY);
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch {
      return new Set();
    }
  });

  const fetchAnnouncement = useCallback(async () => {
    try {
      const res = await fetch(API_URL);
      const data = await res.json();
      if (data.success && !data.hidden) {
        setAnnouncement({html: data.html, digest: data.digest, link: data.link});
      } else {
        setAnnouncement(null);
      }
    } catch {
      // Silently fail — don't block the UI for a status banner
    }
  }, []);

  useEffect(() => {
    fetchAnnouncement();
    const id = setInterval(fetchAnnouncement, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [fetchAnnouncement]);

  const dismiss = () => {
    if (!announcement) return;
    const next = new Set(dismissedDigests);
    next.add(announcement.digest);
    setDismissedDigests(next);
    try {
      localStorage.setItem(DISMISSED_KEY, JSON.stringify([...next]));
    } catch {
      // localStorage might be full/disabled
    }
  };

  if (!announcement || dismissedDigests.has(announcement.digest)) {
    return null;
  }

  return (
    <div className="relative bg-amber-50 border-b border-amber-200">
      <a
        href="https://status.yojo.health"
        target="_blank"
        rel="noopener noreferrer"
        className="block px-4 py-2 pr-10 text-center text-sm text-amber-900 hover:bg-amber-100 transition-colors"
      >
        <span dangerouslySetInnerHTML={{__html: announcement.html}} />
      </a>
      <button
        onClick={dismiss}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-amber-700 hover:text-amber-900 rounded"
        aria-label="Dismiss announcement"
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}
